import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
  DialogContentText,
  List,
  ListItem,
  ListItemIcon,
  Avatar,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axios';
import { getApiUrl } from '../../../utils/urlHelper';
import { canEditEvent } from '../../../utils/adminUtils';

const EventsList = ({ adminStatus }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [eventPermissions, setEventPermissions] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPastEvents, setShowPastEvents] = useState(false);

  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedEventForAttendance, setSelectedEventForAttendance] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const schoolId = localStorage.getItem('schoolId');
        const facultyId = localStorage.getItem('facultyId');
        
        if (!schoolId) {
          throw new Error('School ID not found. You may need to log in again.');
        }
        
        const eventsResponse = await axios.get(`/api/events/?school=${schoolId}`);
        const sortedEvents = [...eventsResponse.data].sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        });
        
        setAllEvents(sortedEvents);
        
        const classesResponse = await axios.get(`/api/classes/?school=${schoolId}&faculty=${facultyId}`);
        setClasses(classesResponse.data);

        if (adminStatus?.isAdmin) {
          const permissions = {};
          for (const event of sortedEvents) {
            permissions[event.id] = await canEditEvent(
              event.school,
              facultyId,
              event.faculty,
              adminStatus
            );
          }
          setEventPermissions(permissions);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.message && err.message.includes('School ID not found')) {
          setError('School information not available. Please log out and log in again.');
        } else {
          setError('Failed to load data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [adminStatus]);

  useEffect(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const schoolId = localStorage.getItem('schoolId');
    const facultyId = localStorage.getItem('facultyId');
    
    let filteredEvents = allEvents;
    
    if (schoolId) {
      filteredEvents = allEvents.filter(event => 
        String(event.school) === String(schoolId)
      );
    }
    
    // Apply search filter regardless of past/future status
    if (searchQuery.trim() !== '') {
      filteredEvents = filteredEvents.filter(event => 
        event.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Split events into upcoming and past
    let upcomingEvents = [];
    let pastEvents = [];
    
    filteredEvents.forEach(event => {
      const eventDate = new Date(event.date);
      if (eventDate >= today) {
        upcomingEvents.push(event);
      } else if (showPastEvents) {
        pastEvents.push(event);
      }
    });
    
    // Sort each category by date
    upcomingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
    pastEvents.sort((a, b) => new Date(b.date) - new Date(a.date)); // Past events in reverse chronological order
    
    // Split upcoming events into faculty's own events and other events
    let facultyUpcomingEvents = [];
    let otherUpcomingEvents = [];
    
    upcomingEvents.forEach(event => {
      if (String(event.faculty) === String(facultyId)) {
        facultyUpcomingEvents.push(event);
      } else {
        otherUpcomingEvents.push(event);
      }
    });
    
    // Combine all events in the desired order
    if (showPastEvents) {
      // Add an initial divider for "Your Events" if faculty has any events
      const dividers = {};
      
      // Always show "Your Events" divider at the beginning if faculty has events
      if (facultyUpcomingEvents.length > 0) {
        dividers.yourEvents = 0;
      }
      
      // Divider between faculty and other faculty events
      if (facultyUpcomingEvents.length > 0 && otherUpcomingEvents.length > 0) {
        dividers.otherFaculty = facultyUpcomingEvents.length;
      }
      
      // Divider between upcoming and past events
      if (upcomingEvents.length > 0 && pastEvents.length > 0) {
        dividers.pastEvents = upcomingEvents.length;
      }
      
      // Store dividers in session storage
      sessionStorage.setItem('eventDividers', JSON.stringify(dividers));
      setEvents([...facultyUpcomingEvents, ...otherUpcomingEvents, ...pastEvents]);
    } else {
      // For non-past events view, only show dividers for faculty's events and others
      const dividers = {};
      
      // Always show "Your Events" divider at the beginning if faculty has events
      if (facultyUpcomingEvents.length > 0) {
        dividers.yourEvents = 0;
      }
      
      // Show divider between faculty and other events if both exist
      if (facultyUpcomingEvents.length > 0 && otherUpcomingEvents.length > 0) {
        dividers.otherFaculty = facultyUpcomingEvents.length;
      }
      
      sessionStorage.setItem('eventDividers', JSON.stringify(dividers));
      setEvents([...facultyUpcomingEvents, ...otherUpcomingEvents]);
    }
    
  }, [searchQuery, allEvents, showPastEvents]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleTogglePastEvents = () => {
    setShowPastEvents(!showPastEvents);
  };

  const handleAssignEvent = (event) => {
    setSelectedEvent(event);
    setSelectedClasses([]);
    setAssignDialogOpen(true);
  };

  const handleAssignSubmit = async () => {
    try {
      const assignmentPromises = selectedClasses.map(classId => 
        axios.post('/api/class-events/', {
          class_instance: classId,
          event: selectedEvent.id
        })
      );
      
      await Promise.all(assignmentPromises);
      
      const schoolId = localStorage.getItem('schoolId');
      
      const response = await axios.get(`/api/events/?school=${schoolId}`);
      const sortedEvents = [...response.data].sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });
      
      setAllEvents(sortedEvents);
      
      setAssignDialogOpen(false);
      setSelectedClasses([]);
    } catch (err) {
      console.error('Error assigning event:', err);
    }
  };

  const handleClassSelectionChange = (event) => {
    const { value } = event.target;
    setSelectedClasses(value);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return { date: 'TBD', time: 'TBD' };
    
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const canManageEvent = (event) => {
    const facultyId = localStorage.getItem('facultyId');
    
    if (event.faculty === facultyId) {
      return true;
    }
    
    if (adminStatus?.isAdmin && adminStatus.adminRole === 'sub') {
      return eventPermissions[event.id];
    }
    
    return false;
  };

  const isEventPast = (eventDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(eventDate) < today;
  };

  const isEventToday = (eventDate) => {
    const today = new Date();
    const eventDay = new Date(eventDate);
    return today.getDate() === eventDay.getDate() && 
           today.getMonth() === eventDay.getMonth() && 
           today.getFullYear() === eventDay.getFullYear();
  };

  const handleDownloadQrCode = (eventId) => {
    window.open(`${getApiUrl(`/api/events/${eventId}/generate-qr/`)}`, '_blank');
  };

  const handleOpenDeleteDialog = (event, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    setLoading(true);
    try {
      const facultyId = localStorage.getItem('facultyId');
      
      await axios.delete(`/api/events/${eventToDelete.id}/`, {
        params: { faculty_id: facultyId }
      });
      
      setAllEvents(prev => prev.filter(event => event.id !== eventToDelete.id));
      
      setError(null);
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete the event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceData = async (eventId) => {
    setLoadingAttendance(true);
    setAttendanceError(null);
    try {
      const facultyId = localStorage.getItem('facultyId');
      if (!facultyId) {
        throw new Error('Faculty ID not found. You may need to log in again.');
      }
      
      const response = await axios.get(`/api/events/${eventId}/attendance/`, {
        params: { faculty_id: facultyId }
      });
      
      // Check if response data has attendance property or is an array directly
      const attendanceRecords = Array.isArray(response.data) ? response.data : 
                               (response.data.attendance || []);
      
      setAttendanceData(attendanceRecords);
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setAttendanceError('Failed to load attendance data. Please try again.');
    } finally {
      setLoadingAttendance(false);
    }
  };
  
  const handleOpenAttendanceDialog = (event, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedEventForAttendance(event);
    setAttendanceDialogOpen(true);
    fetchAttendanceData(event.id);
  };
  
  const handleCloseAttendanceDialog = () => {
    setAttendanceDialogOpen(false);
    setSelectedEventForAttendance(null);
    setAttendanceData([]);
  };

  const handleRefreshAttendance = () => {
    if (selectedEventForAttendance) {
      fetchAttendanceData(selectedEventForAttendance.id);
    }
  };

  const handleDownloadAttendanceCSV = async () => {
    if (!selectedEventForAttendance) return;
    
    try {
      const facultyId = localStorage.getItem('facultyId');
      if (!facultyId) {
        throw new Error('Faculty ID not found. You may need to log in again.');
      }
      
      // Use axios to get the CSV data with responseType as blob
      const response = await axios.get(`/api/events/${selectedEventForAttendance.id}/attendance/csv/`, {
        params: { faculty_id: facultyId },
        responseType: 'blob'
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      
      // Set the file name
      const eventName = selectedEventForAttendance.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const date = new Date().toISOString().split('T')[0];
      a.download = `attendance_${eventName}_${date}.csv`;
      
      // Append the anchor to the body, click it, and then remove it
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err) {
      console.error('Error downloading attendance CSV:', err);
      setAttendanceError('Failed to download attendance data. Please try again.');
    }
  };

  const renderDivider = (index) => {
    const dividers = JSON.parse(sessionStorage.getItem('eventDividers') || '{}');
    
    if (dividers.yourEvents === index) {
      return (
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            my: 2 
          }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                px: 2, 
                color: 'text.secondary',
                fontWeight: 'medium',
                bgcolor: 'background.paper',
                borderRadius: 1,
                py: 0.5
              }}
            >
              Your Events
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>
        </Grid>
      );
    }
    
    if (dividers.otherFaculty === index) {
      return (
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            my: 2 
          }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                px: 2, 
                color: 'text.secondary',
                fontWeight: 'medium',
                bgcolor: 'background.paper',
                borderRadius: 1,
                py: 0.5
              }}
            >
              Other Faculty Events
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>
        </Grid>
      );
    }
    
    if (dividers.pastEvents === index) {
      return (
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            my: 2 
          }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                px: 2, 
                color: 'text.secondary',
                fontWeight: 'medium',
                bgcolor: 'background.paper',
                borderRadius: 1,
                py: 0.5
              }}
            >
              Past Events
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>
        </Grid>
      );
    }
    
    return null;
  };

  return (
    <Box>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#2C2C2C',
            fontWeight: 'bold'
          }}
        >
          Events
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/faculty/events/add')}
          sx={{
            bgcolor: '#DEA514',
            '&:hover': {
              bgcolor: '#B88A10',
            }
          }}
        >
          Create New Event
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Search events by name..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#DEA514',
              },
            },
          }}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch 
                checked={showPastEvents}
                onChange={handleTogglePastEvents}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#DEA514',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#DEA514',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HistoryIcon sx={{ mr: 0.5, fontSize: 20 }} />
                <Typography variant="body2">Show Past Events</Typography>
              </Box>
            }
          />
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: '#DEA514' }} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {events.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {searchQuery 
                  ? 'No events found matching your search.' 
                  : showPastEvents 
                    ? 'No events found. Create your first event!' 
                    : 'No upcoming events found. Create a new event or enable "Show Past Events" to see your history.'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/faculty/events/add')}
                  sx={{
                    mt: 2,
                    bgcolor: '#DEA514',
                    '&:hover': {
                      bgcolor: '#B88A10',
                    }
                  }}
                >
                  Create New Event
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {events.map((event, index) => {
                const { date, time } = formatDateTime(event.date);
                const endTime = event.end_time ? formatDateTime(event.end_time).time : null;
                const eventDate = new Date(event.date);
                const isPast = isEventPast(event.date);
                const isToday = isEventToday(event.date);
                
                return (
                  <React.Fragment key={event.id}>
                    {renderDivider(index)}
                    
                    <Grid item xs={12}>
                      <Paper
                        sx={{
                          p: 3,
                          bgcolor: '#FFFFFF',
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          borderLeft: isPast 
                            ? '4px solid #999' 
                            : isToday 
                              ? '4px solid #4CAF50' 
                              : canManageEvent(event) 
                                ? '4px solid #DEA514' 
                                : '4px solid #90caf9',
                        }}
                      >
                        {/* Event information section (left side) */}
                        <Box sx={{ 
                          flex: 4,
                          pr: { xs: 0, sm: 3 },
                          pb: { xs: 2, sm: 0 },
                          borderBottom: { xs: '1px solid #eee', sm: 'none' },
                          borderRight: { xs: 'none', sm: '1px solid #eee' },
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                          <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {isToday && (
                              <Chip 
                                label="Today" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#4CAF50',
                                  color: 'white',
                                }} 
                              />
                            )}
                            
                            {event.faculty === localStorage.getItem('facultyId') ? (
                              <Chip 
                                label="Your Event" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#DEA514',
                                  color: 'white',
                                }} 
                              />
                            ) : canManageEvent(event) ? (
                              <Chip 
                                label="Admin Access" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#4a148c',
                                  color: 'white',
                                }} 
                              />
                            ) : null}
                          </Box>
                          
                          <Typography variant="h6" sx={{ mb: 1.5, color: '#2C2C2C', fontWeight: 'bold' }}>
                            {event.name}
                          </Typography>
                          
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <CalendarTodayIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                {date}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <AccessTimeIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                {time}{endTime ? ` - ${endTime}` : ''}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LocationOnIcon sx={{ fontSize: 18, mr: 1, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                {event.location || 'No location specified'}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography variant="body2" 
                            sx={{ 
                              mb: 2,
                              color: '#666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                            }}>
                            {event.description || 'No description available'}
                          </Typography>

                          <Box sx={{ mt: 'auto' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                              Check-in Window: {event.checkin_before_minutes > 0 ? `${event.checkin_before_minutes} min before` : 'Starts at event time'} 
                              {' to '}
                              {event.checkin_after_minutes > 0 ? `${event.checkin_after_minutes} min after` : 'event time only'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Action buttons section (right side) */}
                        <Box sx={{ 
                          flex: 1,
                          pl: { xs: 0, sm: 3 },
                          pt: { xs: 2, sm: 0 },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          minWidth: { sm: '140px' }
                        }}>
                          {/* Assign button */}
                          <Tooltip title="Assign to classes">
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              variant="outlined"
                              fullWidth
                              sx={{
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                py: 0.8,
                                '&:hover': {
                                  borderColor: '#3d8b40',
                                  color: '#3d8b40',
                                  backgroundColor: 'rgba(76, 175, 80, 0.04)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssignEvent(event);
                              }}
                            >
                              Assign
                            </Button>
                          </Tooltip>
                          
                          {/* QR Code button */}
                          <Button
                            size="small"
                            startIcon={<QrCode2Icon />}
                            variant="outlined"
                            fullWidth
                            sx={{ 
                              borderColor: '#666',
                              color: '#666',
                              '&:hover': {
                                backgroundColor: 'rgba(102, 102, 102, 0.04)'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadQrCode(event.id);
                            }}
                          >
                            QR Code
                          </Button>
                          
                          {/* Attendance button - only if can manage event */}
                          {canManageEvent(event) && (
                            <Button
                              size="small"
                              startIcon={<PeopleIcon />}
                              variant="outlined"
                              fullWidth
                              sx={{ 
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                }
                              }}
                              onClick={(e) => handleOpenAttendanceDialog(event, e)}
                            >
                              Attendance
                            </Button>
                          )}
                          
                          {/* Edit button - only if can manage event */}
                          {canManageEvent(event) && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              fullWidth
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/faculty/events/${event.id}/edit`);
                              }}
                              sx={{
                                borderColor: '#DEA514',
                                color: '#DEA514',
                                '&:hover': {
                                  borderColor: '#B88A10',
                                  color: '#B88A10',
                                  bgcolor: 'rgba(222, 165, 20, 0.04)',
                                },
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          
                          {/* Delete button - only if can manage event */}
                          {canManageEvent(event) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              fullWidth
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteDialog(event, e);
                              }}
                              sx={{
                                '&:hover': {
                                  bgcolor: 'rgba(211, 47, 47, 0.04)',
                                },
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          )}
        </>
      )}

      <Dialog 
        open={assignDialogOpen} 
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Event to Classes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Select one or more classes to assign this event to:
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="select-classes-label">Select Classes</InputLabel>
            <Select
              labelId="select-classes-label"
              multiple
              value={selectedClasses}
              onChange={handleClassSelectionChange}
              input={<OutlinedInput label="Select Classes" />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select classes</em>;
                }
                return selected.map(id => {
                  const classItem = classes.find(c => c.id === id);
                  return classItem ? classItem.name : '';
                }).join(', ');
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300
                  }
                }
              }}
            >
              {classes.map((classItem) => (
                <MenuItem key={classItem.id} value={classItem.id}>
                  <Checkbox checked={selectedClasses.indexOf(classItem.id) > -1} />
                  <ListItemText primary={classItem.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignSubmit} 
            disabled={selectedClasses.length === 0}
            sx={{ color: '#DEA514' }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Event
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the event "{eventToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteEvent} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={attendanceDialogOpen}
        onClose={handleCloseAttendanceDialog}
        aria-labelledby="attendance-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="attendance-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Live Attendance: {selectedEventForAttendance?.name}
          </Typography>
          <Box sx={{ display: 'flex' }}>
            <Tooltip title="Download attendance as CSV">
              <IconButton 
                onClick={handleDownloadAttendanceCSV} 
                disabled={loadingAttendance || attendanceData.length === 0}
                sx={{ mr: 1 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh attendance data">
              <IconButton onClick={handleRefreshAttendance} disabled={loadingAttendance}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingAttendance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#DEA514' }} />
            </Box>
          ) : attendanceError ? (
            <Alert severity="error">{attendanceError}</Alert>
          ) : attendanceData.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No attendance records yet for this event.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                Total Attendees: {attendanceData.length}
              </Typography>
              <List>
                {attendanceData.map((record) => (
                  <ListItem key={record.id} divider>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#DEA514' }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1">
                        {record.student_name || 'Unnamed Student'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Checked in: {new Date(record.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttendanceDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsList;